import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  DownloadReportAction,
  requestOtp,
} from 'src/app/loaders/download_report_loaders';
import LightLogoIcon from 'src/assets/artera_text_logo_light.svg';
import { notifyError } from 'src/utils';

const BACKGROUND_IMAGE_URL = '/content/auth0-background.png';

const RESEND_COOLDOWN_SECONDS = 30;

const PageContainer = styled(Box)({
  minHeight: '100vh',
  width: '100%',
  backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
});

const DialogCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 440,
  width: '100%',
  boxShadow: theme.shadows[8],
  borderRadius: 16,
}));

const LogoImg = styled('img')({
  height: 50,
  width: 'auto',
  display: 'block',
  margin: '30px 0px 10px 0px',
});

const ERROR_MESSAGES = {
  incorrect: 'Incorrect code. Please try again or click resend below.',
  expired: 'Your code has expired. Please request a new one.',
  rate_limit: 'Too many requests. Please wait before trying again.',
  unknown: 'Failed to download report. Please try again later.',
};

export function DownloadReportPage() {
  const { token } = useParams<{ token: string }>();
  if (!token) {
    return (
      <PageContainer>
        <Typography
          sx={{
            color: 'text.primary',
          }}
        >
          Invalid link. Missing token.
        </Typography>
      </PageContainer>
    );
  }
  return <DownloadReportContent token={token} />;
}

function DownloadReportContent({ token }: { token: string }) {
  const { data: otpResult } = useSuspenseQuery({
    queryKey: ['downloadReportOtp', token],
    queryFn: () => requestOtp(token),
  });
  const fetcher = useFetcher<{
    action?: DownloadReportAction;
    url?: string;
    error?: keyof typeof ERROR_MESSAGES;
  }>();
  const [accessCode, setAccessCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState<number | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    if (cooldownRef.current) {
      clearInterval(cooldownRef.current);
      cooldownRef.current = null;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    let remaining = RESEND_COOLDOWN_SECONDS;
    cooldownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        if (cooldownRef.current) {
          clearInterval(cooldownRef.current);
          cooldownRef.current = null;
        }
        setCooldown(null);
      } else {
        setCooldown(remaining);
      }
    }, 1000);
  }, []);

  // Show error from initial loader OTP result; start cooldown after initial OTP send
  useEffect(() => {
    if (!otpResult) return;
    if (otpResult.ok) {
      startCooldown();
      return;
    }
    if (otpResult.rateLimited) {
      setErrorMessage(ERROR_MESSAGES.rate_limit);
    } else {
      notifyError(
        'The link has expired. Please contact the physician to request a new link.',
      );
    }
  }, [otpResult, startCooldown]);

  // Handle unlock action result: redirect on success, show error on failure
  useEffect(() => {
    if (fetcher.data?.action !== DownloadReportAction.DOWNLOAD_SHARED_REPORT)
      return;
    if (fetcher.data?.url) {
      window.location.href = fetcher.data.url;
    } else if (fetcher.data?.error) {
      setErrorMessage(ERROR_MESSAGES[fetcher.data.error]);
    }
  }, [fetcher.data]);

  const handleRequestOtp = useCallback(() => {
    if (!token) return;
    setErrorMessage(null);
    startCooldown();
    fetcher.submit(
      { actionType: DownloadReportAction.REQUEST_OTP, token },
      { method: 'POST' },
    );
  }, [token, startCooldown, fetcher]);

  const handleUnlockDownload = useCallback(() => {
    if (!token || !accessCode.trim()) return;
    setErrorMessage(null);
    fetcher.submit(
      {
        actionType: DownloadReportAction.DOWNLOAD_SHARED_REPORT,
        token,
        otp: accessCode.trim(),
      },
      { method: 'POST' },
    );
  }, [token, accessCode, fetcher]);

  const isDownloading =
    fetcher.state !== 'idle' &&
    fetcher.formData?.get('actionType') ===
      DownloadReportAction.DOWNLOAD_SHARED_REPORT;

  return (
    <PageContainer>
      <DialogCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <LogoImg src={LightLogoIcon} alt='Artera' />
          <Typography
            variant='h5'
            sx={{
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            Access Code Required
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            An access code has been sent to your email. Please enter the code
            below to unlock and download the encrypted report.
          </Typography>
          <TextField
            label='Enter Access Code'
            type='password'
            autoComplete='one-time-code'
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage || ' '}
            sx={{ margin: '14px 0px 0px 0px' }}
            fullWidth
            size='medium'
          />
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {"Didn't receive the email? "}
            {cooldown !== null ? (
              <Typography
                component='span'
                sx={{
                  color: 'text.disabled',
                }}
              >
                Resend ({String(cooldown).padStart(2, '0')}s)
              </Typography>
            ) : (
              <Typography
                component='span'
                sx={{ cursor: 'pointer' }}
                color='primary'
                onClick={handleRequestOtp}
                data-testid='resend-link'
              >
                Resend
              </Typography>
            )}
          </Typography>
          <Button
            variant='contained'
            size='large'
            sx={{ width: '60%', alignSelf: 'center' }}
            disabled={!accessCode.trim() || isDownloading}
            onClick={handleUnlockDownload}
            data-testid='unlock-download-button'
          >
            {isDownloading ? 'Loading…' : 'UNLOCK & DOWNLOAD'}
          </Button>
        </Box>
      </DialogCard>
    </PageContainer>
  );
}
